import type {
  AttributeChoice,
  AttributeDefinition,
  LanguageMap,
  OptionalLanguageMap,
} from "@/services/category.service";

export const ATTRIBUTE_VALUE_TYPES = ["string", "number", "integer", "boolean", "enum"] as const;
export type AttributeValueType = (typeof ATTRIBUTE_VALUE_TYPES)[number];

export const ATTRIBUTE_TYPE_LABELS: Record<AttributeValueType, string> = {
  string: "Text",
  number: "Number",
  integer: "Whole number",
  boolean: "Yes/No",
  enum: "Choice",
};

export interface AttributeValueSettings {
  valueType: AttributeValueType;
  description: string;
  attribute_slug?: OptionalLanguageMap;
  choices: AttributeChoice[];
}

export function enumChoicesError(choices: readonly AttributeChoice[]): string | null {
  const values = choices.map((choice) =>
    typeof choice.value === "string" ? choice.value.trim() : choice.value,
  );
  if (!values.length) return "Add at least one choice.";
  if (values.some((value) => value === "")) return "Every choice needs a value.";
  if (values.some((value) => typeof value === "string" && value.includes("\u0000"))) {
    return "Choices cannot contain null characters.";
  }
  const identities = values.map((value) => `${typeof value}:${JSON.stringify(value)}`);
  if (new Set(identities).size !== identities.length) return "Each choice must be unique.";
  return null;
}

export function attributeValueSettings(definition: AttributeDefinition): AttributeValueSettings {
  return {
    valueType: definition.type === "string" && definition.choices ? "enum" : definition.type,
    description: definition.description ?? "",
    attribute_slug: definition.slug,
    choices: (definition.choices ?? []).map((choice) => ({
      ...choice,
      label: { ...choice.label },
      ...(choice.slug ? { slug: { ...choice.slug } } : {}),
    })),
  };
}

export function attributeDefinition(
  settings: AttributeValueSettings,
  label: LanguageMap,
  languages: readonly string[],
): AttributeDefinition {
  if (languages.some((language) => !label[language]?.trim())) {
    throw new Error("Every attribute needs a label in every catalogue language.");
  }
  const normalizedLabel = Object.fromEntries(
    languages.map((language) => [language, label[language].trim()]),
  );
  const normalizedSlug = Object.fromEntries(
    languages.flatMap((language) => {
      const value = settings.attribute_slug?.[language]?.trim();
      return value ? [[language, value]] : [];
    }),
  );
  const choices = settings.choices.map((choice) => ({
    value: typeof choice.value === "string" ? choice.value.trim() : choice.value,
    label: Object.fromEntries(
      languages.map((language) => [language, choice.label[language]?.trim() ?? ""]),
    ),
    ...(choice.slug
      ? {
          slug: Object.fromEntries(
            languages.flatMap((language) => {
              const value = choice.slug?.[language]?.trim();
              return value ? [[language, value]] : [];
            }),
          ),
        }
      : {}),
  }));
  if (choices.some((choice) => languages.some((language) => !choice.label[language]))) {
    throw new Error("Every configured value needs a label in every catalogue language.");
  }
  if (settings.valueType === "enum") {
    const error = enumChoicesError(choices);
    if (error) throw new Error(error);
  }
  if (settings.valueType === "boolean") {
    const values = new Set(choices.map((choice) => choice.value));
    if (values.size !== 2 || !values.has(true) || !values.has(false)) {
      throw new Error("Yes/No attributes require labels for true and false.");
    }
  }
  const description = settings.description.trim();
  return {
    type: settings.valueType === "enum" ? "string" : settings.valueType,
    ...(description ? { description } : {}),
    label: normalizedLabel,
    ...(Object.keys(normalizedSlug).length ? { slug: normalizedSlug } : {}),
    ...(choices.length ? { choices } : {}),
  };
}
